import {
  basketHasDeliverables,
  basketPurchasable,
  totalDeliverables,
} from "lib/basket/helpers";
import { useGetAddressForAccountQuery } from "lib/graphql-generated";
import { useBasket } from "lib/hooks/basket/use-basket";
import { useInput } from "lib/hooks/use-input";
import { useUser } from "lib/hooks/use-user";
import { Spinner } from "components/spinner";
import { AddressSection } from "./checkout/address";
import { useEffect, useState } from "react";
import {
  ShippingSelector,
  ShippingSelectorValue,
} from "components/forms/shipping-selector";
import { CountrySelector } from "components/forms/country-select";
import { ClientOnly } from "components/client-only";
import { NO_DELIVERABLES_SHIPPING_ID, PICKUP_SHIPPING_ID } from "lib/constants";
import { shippingIsValid } from "lib/shipping-is-valid";
import { FormattedAddress } from "components/address";

export const CheckoutSection = () => {
  const { userData } = useUser();
  const [basket, modifyBasket] = useBasket();

  const shipping = useInput<ShippingSelectorValue>({
    id: "",
    price: null,
    name: "",
  });

  const { data, loading, refetch } = useGetAddressForAccountQuery({
    variables: {
      uuid: userData?.accountId,
    },
    skip: !userData?.accountId,
    fetchPolicy: "network-only",
  });

  let defaultAddress = data?.accounts_by_pk?.primary_address;

  useEffect(() => {
    if (!!defaultAddress) {
      modifyBasket({
        type: "set-address",
        payload: {
          account_id: userData?.accountId,
          contact_email: defaultAddress.contact_email,
          phone_number: defaultAddress.phone_number,
          line_1: defaultAddress.line_1,
          line_2: defaultAddress?.line_2,
          line_3: defaultAddress?.line_3,
          id: defaultAddress.id,
          city: defaultAddress.city,
          code: defaultAddress.code,
          country_code: defaultAddress.country?.code,
          name: defaultAddress.name,
          region: defaultAddress.region,
        },
      });
    }
  }, [defaultAddress]);

  useEffect(() => {
    if (!basketHasDeliverables(basket) && basketPurchasable(basket)) {
      modifyBasket({
        type: "set-shipping",
        payload: NO_DELIVERABLES_SHIPPING_ID,
      });
    } else {
      modifyBasket({ type: "set-shipping", payload: shipping.value.id });
    }
  }, []);

  useEffect(() => {
    if (!basketHasDeliverables(basket) && basketPurchasable(basket)) {
      modifyBasket({
        type: "set-shipping",
        payload: NO_DELIVERABLES_SHIPPING_ID,
      });
    }
  }, [basket.items, basket.pif]);

  return (
    <>
      <section className='flex flex-col'>
        {!basketHasDeliverables(basket) ? (
          <p className='pb-2'>
            You have no items that require shipping - your address details will
            be used as contact details.
          </p>
        ) : (
          <div className='relative border rounded-md p-4 flex flex-col mb-4'>
            <CountrySelector
              label='Country'
              value={basket.address?.country_code || ""}
              setValue={(v) => {
                modifyBasket({
                  type: "set-address",
                  payload: {
                    ...basket.address,
                    country_code: v.toString(),
                  },
                });
              }}
              className='col-span-full'
            />
            <ClientOnly>
              <ShippingSelector
                country={basket.address?.country_code || ""}
                shippableProducts={totalDeliverables(basket)}
                value={shipping.value}
                setValue={(v) => {
                  modifyBasket({ type: "set-shipping", payload: v["id"] });
                  shipping.setValue(v);
                }}
                label='Shipping Method'
                className='mt-4'
              />
            </ClientOnly>
          </div>
        )}

        <div className='bg-white border border-gray-200 divide-y rounded-md -space-y-px'>
          {!shippingIsValid(basket) && (
            <section className='bg-yellow-50 p-4 rounded-md'>
              <h3 className='text-yellow-900 text-sm'>Reuired Details</h3>
              <ul className='text-xs text-yellow-700'>
                <li>Name</li>
                <li>Email</li>
                <li>Contact Number</li>
                {basket.shippingOptionId !== PICKUP_SHIPPING_ID && (
                  <>
                    <li>Line 1</li>
                    <li>City</li>
                    <li>Post Code</li>
                  </>
                )}
              </ul>
            </section>
          )}
          {loading ? (
            <Spinner />
          ) : (
            !!defaultAddress && (
              <section className='relative  p-4 flex'>
                <input
                  className='focus:ring-green-default h-4 w-4 mt-1 text-green-default cursor-pointer border-gray-300'
                  type='radio'
                  name='shipping-address'
                  id='useDefaultShipping'
                  checked={basket.address?.id === defaultAddress.id}
                  onChange={(e) => {
                    if (e.target.checked) {
                      modifyBasket({
                        type: "set-address",
                        payload: {
                          account_id: userData?.accountId,
                          contact_email: defaultAddress.contact_email,
                          phone_number: defaultAddress.phone_number,
                          line_1: defaultAddress.line_1,
                          line_2: defaultAddress.line_2,
                          line_3: defaultAddress.line_3,
                          id: defaultAddress.id,
                          city: defaultAddress.city,
                          code: defaultAddress.code,
                          country_code: defaultAddress.country.code,
                          name: defaultAddress.name,
                          region: defaultAddress.region,
                        },
                      });
                    }
                  }}
                />
                <label
                  htmlFor='useDefaultShipping'
                  className='ml-3 flex flex-col cursor-pointer'>
                  <h3 className='font-bold text-sm'>
                    Use my default shipping details
                  </h3>

                  <FormattedAddress address={defaultAddress} />
                </label>
              </section>
            )
          )}
          {data?.accounts_by_pk?.addresses?.map(
            (address) =>
              address.id !== defaultAddress?.id && (
                <section
                  className='relative p-4 flex'
                  key={`select_${address.id}`}>
                  <input
                    className='focus:ring-green-default h-4 w-4 mt-1 text-green-default cursor-pointer border-gray-300'
                    type='radio'
                    name='shipping-address'
                    id={`other_${address.id}`}
                    checked={basket.address?.id === address.id}
                    onChange={(e) => {
                      if (e.target.checked) {
                        modifyBasket({
                          type: "set-address",
                          payload: {
                            account_id: userData?.accountId,
                            contact_email: address.contact_email,
                            phone_number: address.phone_number,
                            line_1: address.line_1,
                            line_2: address.line_2,
                            line_3: address.line_3,
                            id: address.id,
                            city: address.city,
                            code: address.code,
                            country_code: address.country?.code,
                            name: address.name,
                            region: address.region,
                          },
                        });
                      }
                    }}
                  />
                  <label
                    htmlFor={`other_${address.id}`}
                    className='ml-3 flex flex-col cursor-pointer'>
                    <FormattedAddress address={address} />
                  </label>
                </section>
              )
          )}
          <section className='relative  p-4 flex flex-wrap'>
            <input
              className='focus:ring-green-default h-4 w-4 mt-1 text-green-default cursor-pointer border-gray-300'
              type='radio'
              name='shipping-address'
              id='useCustomShiping'
              checked={!basket.address?.id}
              onChange={(e) => {
                if (e.target.checked)
                  modifyBasket({
                    type: "set-address",
                    payload: {
                      account_id: userData?.accountId,
                    },
                  });
              }}
            />
            <label
              htmlFor='useCustomShiping'
              className='ml-3 flex flex-col cursor-pointer'>
              <h3 className='font-bold text-sm'>New shipping address</h3>
            </label>
            {!basket.address?.id && (
              <fieldset className=' mt-2 md:grid grid-cols-2 gap-2 gap-y-8'>
                <AddressSection />
              </fieldset>
            )}
          </section>
        </div>
      </section>
    </>
  );
};
